import { debounce, throttle, prefersReducedMotion, isRTL } from '@theme/utilities';

/**
 * Timeout duration (in milliseconds) after which scroll is considered to have ended.
 * @constant {number}
 */
const SCROLL_END_TIMEOUT = 50;

/**
 * Class representing a Scroller that handles smooth scrolling and detects scroll end events.
 *
 * @class
 */
export class Scroller {
  /**
   * The element to apply scrolling to.
   * @type {HTMLElement}
   */
  element;

  /**
   * Promise that resolves when scrolling ends.
   * @type {Promise<void>|undefined}
   */
  #promise = undefined;

  /**
   * Function to resolve the scroll end promise.
   * @type {Function|undefined}
   */
  #resolve = undefined;

  /**
   * Callback function to call while user is scrolling, throttled every 50ms.
   * @type {() => void}
   */
  #throttledCallback;

  /**
   * Callback function invoked after user scroll ends.
   * @type {() => void}
   */
  #endCallback;

  /**
   * Callback function invoked when scrolling starts, regardless of whether it was triggered by a user event.
   * @type {(() => void) | undefined}
   */
  #onScrollInit;

  /**
   * Callback function invoked when scrolling ends, regardless of whether it was triggered by a user event.
   * @type {(() => void) | undefined}
   */
  #onScrollEnd;

  /**
   * Whether the scroll was triggered by a user event.
   * @type {boolean}
   */
  #userEvent = true;

  /**
   * Whether the next scroll event should be ignored.
   * @type {boolean}
   */
  #ignore = false;

  /**
   * Whether the element is currently scrolling.
   * @type {boolean}
   */
  #isScrolling = false;

  /**
   * Creates a Scroller instance.
   *
   * @param {HTMLElement} element - The element to apply scrolling to.
   * @param {Object} options - The options for the scroller.
   * @param {() => void} options.onScroll - Function to call while scrolling and after scrolling ends.
   * @param {() => void} [options.onScrollStart] - Function to call when scrolling starts.
   * @param {() => void} [options.onScrollEnd] - Function to call after scrolling ends.
   * @param {'x' | 'y'} [options.axis] - Scroll axis, when the caller already knows it.
   */
  constructor(element, options) {
    this.#throttledCallback = throttle(options.onScroll, SCROLL_END_TIMEOUT);
    this.#endCallback = options.onScroll;

    this.#onScrollInit = options.onScrollStart;
    this.#onScrollEnd = options.onScrollEnd;

    this.element = element;
    // Axe déclaré par l'appelant : `getScrollAxis` lit quatre dimensions de la piste, et
    // ces lectures tombent juste après l'écriture de `scroll-snap-type` — donc en plein
    // recalcul forcé.
    if (options.axis === 'x' || options.axis === 'y') this.#declaredAxis = options.axis;

    this.element.addEventListener('scroll', this.#handleScroll);
  }

  /** @type {'x' | 'y' | undefined} */
  #declaredAxis;

  /** @type {number | undefined} */
  #padding;

  /** @type {number} */
  #paddingGeneration = -1;

  /* Retrait de début de la piste. */
  get #paddingStart() {
    if (this.#padding !== undefined && this.#paddingGeneration === resizeGeneration) return this.#padding;

    const value = calculatePaddingStart(this.element, this.axis);
    if (Number.isFinite(value)) {
      this.#padding = value;
      this.#paddingGeneration = resizeGeneration;
    }

    return Number.isFinite(value) ? value : 0;
  }

  /**
   * Scrolls to a specific position or element.
   * @param {number | HTMLElement} input - The position in pixels or an element to scroll to.
   * @param {Object} [options] - Options for the scroll.
   * @param {boolean} [options.instant] - Whether to scroll instantly.
   */
  async to(input, options) {
    let value;

    if (input instanceof HTMLElement) {
      const paddingStart = this.#paddingStart;
      // En RTL, scrollLeft part de 0 et devient négatif : on aligne le bord droit de l'élément.
      value = this.#rtl
        ? input.offsetLeft + input.offsetWidth - this.element.clientWidth + paddingStart
        : input[`offset${this.#edge}`] - paddingStart;
    } else {
      value = input;
    }

    const currentPosition = this.element[`scroll${this.#edge}`];
    const willChange = currentPosition !== value;

    if (willChange) {
      this.#scroll({ ...options, method: 'scrollTo', value });
    } else if (this.#isScrolling) {
      // If the scroll has started but then it's released in the same original position,
      // the scroll event will not fire, so we need to manually trigger the scroll end.
      this.#handleScrollEnd(false);
    }
  }

  /**
   * Scrolls by a certain number of pixels.
   * @param {number} value - The number of pixels to scroll by.
   * @param {Object} [options] - Options for the scroll.
   * @param {boolean} [options.instant] - Whether to scroll instantly.
   */
  by(value, options) {
    this.#scroll({ ...options, method: 'scrollBy', value });
  }

  /**
   * Scrolls the element.
   * @param {Object} options - The options for the scroll.
   * @param {'scrollTo' | 'scrollBy'} options.method - The method to use to scroll.
   * @param {number} options.value - The value to scroll to.
   * @param {boolean} [options.instant] - Whether to scroll instantly.
   */
  #scroll(options) {
    const { method, value } = options;
    const instant = options.instant ?? (prefersReducedMotion() || document.readyState !== 'complete');

    this.#reset();
    this.#ignore = instant;
    this.#userEvent = false;

    const currentPosition = this.element[`scroll${this.#edge}`];
    const request = method === 'scrollBy' ? currentPosition + value : value;
    const targetPosition = instant ? request : this.#bound(request);
    const scrollDistance = Math.abs(targetPosition - currentPosition);

    // If the distance is negligible, don't scroll and resolve immediately
    if (scrollDistance < 1) {
      return Promise.resolve();
    }

    if (instant) {
      this.element[method]({ [this.#edge.toLowerCase()]: value, behavior: 'instant' });
      return Promise.resolve();
    }

    this.#setup();
    return this.#ramp(currentPosition, targetPosition);
  }

  /**
   * Borne une position à la course réellement parcourable de la piste.
   * @param {number} target
   * @returns {number}
   */
  #bound(target) {
    const travel =
      this.#edge === 'Left'
        ? this.element.scrollWidth - this.element.clientWidth
        : this.element.scrollHeight - this.element.clientHeight;

    const max = Math.max(0, travel);
    return this.#rtl ? Math.min(0, Math.max(target, -max)) : Math.max(0, Math.min(target, max));
  }

  get #rtl() {
    return this.axis === 'x' && isRTL(this.element);
  }

  /** @type {number|undefined} Image demandée pour la rampe en cours. */
  #rampImage = undefined;

  /** @type {((position: number) => void)|undefined} Prévenu à chaque pas de rampe. */
  #onRamp = undefined;

  /** Une rampe est-elle en train de mener la piste ? */
  get onRamp() {
    return this.#rampImage !== undefined;
  }

  /**
   * Déclare qui suivre les pas de la rampe.
   * @param {(position: number) => void} callback
   */
  followsRamp(callback) {
    this.#onRamp = callback;
  }

  /** @type {string|undefined} Aimantation d'origine, le temps de la rampe. */
  #snapBefore = undefined;

  /**
   * @param {number} from
   * @param {number} target
   * @returns {Promise<void>}
   */
  #ramp(from, target) {
    const edge = this.#edge.toLowerCase();

    // La cible arrive déjà bornée par `#bound`, sinon la courbe se calculerait sur une
    // distance qui n'existe pas et la piste arriverait au bout dès les premières images.
    const distance = target - from;

    // Garde-fou : on dénoue la promesse d'arrivée, personne d'autre ne le fera.
    if (Math.abs(distance) < 1) {
      this.#reset();
      return Promise.resolve();
    }

    const duration = Math.min(560, Math.max(280, Math.abs(distance) * 0.55));

    /*
     * L'aimantation est suspendue le temps de la rampe. En `mandatory`, elle ramenait
     * chaque position intermédiaire sur la diapo la plus proche : la piste restait
     * collée au départ puis sautait d'un bloc à l'arrivée. On la rend telle qu'on l'a
     * trouvée, une fois posé sur la diapo visée — qui est un point d'aimantation.
     */
    this.#snapBefore = this.element.style.getPropertyValue('scroll-snap-type');
    this.element.style.setProperty('scroll-snap-type', 'none');

    return new Promise((resolve) => {
      // L'horloge démarre à la PREMIÈRE image peinte : la prendre à l'appel comptait
      // comme écoulé le temps passé à finir la tâche en cours.
      /** @type {number | undefined} */
      let start;
      /** @param {number} now */
      const step = (now) => {
        if (start === undefined) start = now;

        const progress = Math.min(1, (now - start) / duration);
        const eased =
          progress < 0.5
            ? 4 * progress * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 3) / 2;

        const position = from + distance * eased;
        this.element.scrollTo({ [edge]: position, behavior: 'instant' });

        /*
         * La rampe ANNONCE la position qu'elle vient d'écrire.
         *
         * Sans ça, l'écouteur de défilement la relisait à l'image suivante — une lecture
         * juste après une écriture, donc un recalcul de toute la page à chaque image.
         * Elle est connue ici, autant la donner : 450 ms de recalcul forcé en moins sur
         * un mobile au processeur ralenti, relevés à la trace.
         */
        this.#onRamp?.(position);

        if (progress < 1) {
          this.#rampImage = requestAnimationFrame(step);
          return;
        }

        this.#rampImage = undefined;
        this.#restoreSnap();
        resolve();
      };
      this.#rampImage = requestAnimationFrame(step);
    });
  }

  /** Remet l'aimantation telle qu'elle était avant la rampe. */
  #restoreSnap() {
    if (this.#snapBefore === undefined) return;

    if (this.#snapBefore === '') this.element.style.removeProperty('scroll-snap-type');
    else this.element.style.setProperty('scroll-snap-type', this.#snapBefore);

    this.#snapBefore = undefined;
  }

  /**
   * Gets the scrolling axis ('x' or 'y') based on the element's dimensions.
   * @type {'x' | 'y'}
   * @readonly
   */
  get axis() {
    if (this.#declaredAxis) return this.#declaredAxis;

    // `getScrollAxis` lit quatre dimensions de la piste.
    if (this.#axis && this.#axisGeneration === resizeGeneration) return this.#axis;

    const axis = getScrollAxis(this.element);

    // Une piste sans mise en page mesure 0 et ferait retomber la fonction sur
    // 'y' par défaut : on ne fige rien tant qu'elle n'a pas de dimensions.
    if (this.element.clientWidth > 0 || this.element.clientHeight > 0) {
      this.#axis = axis;
      this.#axisGeneration = resizeGeneration;
    }

    return axis;
  }

  /** @type {'x' | 'y' | undefined} */
  #axis;

  /** @type {number} */
  #axisGeneration = -1;

  /**
   * Promise that resolves when scrolling ends.
   * @type {Promise<void>}
   * @readonly
   */
  get finished() {
    return this.#promise ?? Promise.resolve();
  }

  /**
   * Gets the scroll edge property ('Left' or 'Top') based on the axis.
   * @returns {'Left' | 'Top'}
   */
  get #edge() {
    return this.axis === 'x' ? 'Left' : 'Top';
  }

  /**
   * Sets up the scroll end promise if not already set.
   */
  #setup() {
    if (this.#promise) {
      return;
    }

    this.#promise = new Promise((resolve) => (this.#resolve = resolve));
  }

  #reset = () => {
    // Une nouvelle demande annule la rampe en cours, sinon les deux s'écrivent dessus.
    if (this.#rampImage !== undefined) {
      cancelAnimationFrame(this.#rampImage);
      this.#rampImage = undefined;
      this.#restoreSnap();
    }

    this.#handleScrollEnd.cancel();
    this.#resolve?.();

    this.#promise = undefined;
    this.#resolve = undefined;
    this.#userEvent = true;
    this.#ignore = false;
  };

  /**
   * Event handler for the 'scroll' event.
   */
  #handleScroll = () => {
    if (!this.#isScrolling) {
      this.#onScrollInit?.();
      this.#isScrolling = true;
    }

    if (this.#ignore) {
      this.#reset();
      this.#handleScrollEnd(false);
      return;
    }

    const userEvent = this.#userEvent;

    this.#setup();
    if (userEvent) this.#throttledCallback();
    this.#handleScrollEnd(userEvent);
  };

  /**
   * Handler called when scrolling has ended.
   */
  #handleScrollEnd = debounce(
    /**
     * @param {boolean} userEvent
     */
    (userEvent) => {
      this.#resolve?.();
      if (userEvent) this.#endCallback();
      this.#reset();

      if (this.#isScrolling) {
        this.#onScrollEnd?.();
        this.#isScrolling = false;
      }
    },
    SCROLL_END_TIMEOUT
  );

  /**
   * Sets the scroll snap behavior of the element.
   * @param {boolean} value - Whether to enable scroll snap.
   */
  set snap(value) {
    // Changing the snap behavior will trigger a scroll event, which we should ignore
    this.#ignore = true;
    this.element.style.setProperty('scroll-snap-type', value ? `${this.axis} mandatory` : 'none');
  }

  /**
   * Destroys the Scroller instance.
   */
  destroy() {
    this.element.removeEventListener('scroll', this.#handleScroll);
  }
}

/* Incrémenté à chaque redimensionnement de la fenêtre. */
let resizeGeneration = 0;
if (typeof window !== 'undefined') {
  window.addEventListener('resize', () => resizeGeneration++, { passive: true });
}

/**
 * Gets the scroll axis ('x' or 'y') based on the element's dimensions.
 * @param {HTMLElement} el - The element to get the scroll axis of.
 * @returns {'x' | 'y'}
 */
function getScrollAxis(el) {
  if (el.scrollHeight > el.clientHeight && el.scrollWidth === el.clientWidth) {
    return 'y';
  }

  if (el.scrollWidth > el.clientWidth && el.scrollHeight === el.clientHeight) {
    return 'x';
  }

  return el.scrollWidth > el.scrollHeight ? 'x' : 'y';
}

/**
 * Calculates the padding-start around an element to update the scroll offset.
 * @param {HTMLElement} element - The element to calculate the padding-start of.
 * @param {'x' | 'y'} axis - The axis to calculate the padding-start of.
 * @returns {number} The padding-start in pixels.
 */
function calculatePaddingStart(element, axis) {
  const computedStyle = getComputedStyle(element);
  const value = axis === 'x' ? computedStyle.paddingInlineStart : computedStyle.paddingBlockStart;

  return parseFloat(value);
}

/**
 * Scrolls an element into view.
 * @param {Element} element - The element to scroll into view.
 * @param {Object} options - The options for the scroll.
 * @param {ScrollBehavior} [options.behavior='smooth'] - The behavior of the scroll.
 * @param {'start' | 'center' | 'end'} [options.block='start'] - The block alignment of the element.
 * @param {'start' | 'center' | 'end'} [options.inline='start'] - The inline alignment of the element.
 * @param {Element} [options.ancestor] - The ancestor element to scroll into view.
 */
export function scrollIntoView(element, { ancestor, behavior = 'smooth', block = 'start', inline = 'start' } = {}) {
  if (!ancestor) {
    return element.scrollIntoView({ behavior, block, inline });
  }

  const elemRect = element.getBoundingClientRect();
  const ancestorRect = ancestor.getBoundingClientRect();

  // Rect positions are physical, so logical inline alignments swap sides in RTL
  const rtl = isRTL(ancestor);
  /** @type {'start' | 'center' | 'end'} */
  let inlineAlignment = inline;
  if (rtl && inline === 'start') inlineAlignment = 'end';
  if (rtl && inline === 'end') inlineAlignment = 'start';

  /**
   * Calculates the scroll offset for an element.
   * @param {'start' | 'center' | 'end'} alignment - The alignment of the element.
   * @param {number} ancestorStart - The start of the ancestor element.
   * @param {number} ancestorLength - The length of the ancestor element.
   * @param {number} elemStart - The start of the element.
   * @param {number} elemLength - The length of the element.
   * @param {number} currentScroll - The current scroll position.
   * @returns {number} The scroll offset.
   */
  const calculateScrollOffset = (alignment, ancestorStart, ancestorLength, elemStart, elemLength, currentScroll) => {
    switch (alignment) {
      case 'start':
        return currentScroll + elemStart - ancestorStart;
      case 'center':
        return currentScroll + elemStart - ancestorStart - ancestorLength / 2 + elemLength / 2;
      case 'end':
        return currentScroll + elemStart - ancestorStart - ancestorLength + elemLength;
      default:
        return currentScroll;
    }
  };

  const scrollTop =
    ancestor.scrollHeight > ancestor.clientHeight
      ? calculateScrollOffset(
          block,
          ancestorRect.top,
          ancestor.clientHeight,
          elemRect.top,
          elemRect.height,
          ancestor.scrollTop
        )
      : ancestor.scrollTop;

  const scrollLeft =
    ancestor.scrollWidth > ancestor.clientWidth
      ? calculateScrollOffset(
          inlineAlignment,
          ancestorRect.left,
          ancestor.clientWidth,
          elemRect.left,
          elemRect.width,
          ancestor.scrollLeft
        )
      : ancestor.scrollLeft;

  ancestor.scrollTo({ top: scrollTop, left: scrollLeft, behavior });
}

// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. Slideshow)
class ScrollHint extends HTMLElement {
  /** @type {number | null} */
  #rafId = null;

  connectedCallback() {
    this.addEventListener('scroll', this.#handleScroll);
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    this.removeEventListener('scroll', this.#handleScroll);
    this.#resizeObserver.disconnect();
    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }

  #handleScroll = () => {
    // Throttle scroll updates with requestAnimationFrame to avoid layout thrashing
    if (this.#rafId !== null) return;

    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#update();
    });
  };

  #update = () => {
    const { scrollTop, scrollHeight, clientHeight, scrollLeft, scrollWidth, clientWidth } = this;
    const scrollDirection = scrollWidth > clientWidth ? 'horizontal' : 'vertical';
    // In RTL, scrollLeft is 0 at rest and goes negative, so use the absolute distance
    const rtl = scrollDirection === 'horizontal' && isRTL(this);
    const scrollPercentage =
      scrollDirection === 'vertical'
        ? scrollTop / (scrollHeight - clientHeight)
        : Math.abs(scrollLeft) / (scrollWidth - clientWidth);
    const gradientDirection = scrollDirection === 'vertical' ? 'bottom' : rtl ? 'left' : 'right';

    this.style.maskImage = Number.isNaN(scrollPercentage)
      ? ''
      : `linear-gradient(
        to ${gradientDirection},
        transparent ${scrollPercentage > 0 ? 1 : 0}%,
        black ${scrollPercentage < 0.1 ? scrollPercentage * 100 : 10}%,
        black ${scrollPercentage > 0.9 ? scrollPercentage * 100 : 90}%,
        transparent 100%
      )`;
  };

  #resizeObserver = new ResizeObserver(this.#update);
}

if (!customElements.get('scroll-hint')) {
  customElements.define('scroll-hint', ScrollHint);
}
